export type UserMediaWorkspaceScenarioKey =
  | "avatar"
  | "pet_profile"
  | "daily_record"
  | "receipt"
  | "health_record"
  | "community_post"
  | "unknown";

export type UserMediaWorkspaceFocus = {
  artifactId?: string;
  fileId?: string;
  scenarioKey?: UserMediaWorkspaceScenarioKey;
};

type MediaArtifactFocusRow = {
  artifactId: string;
  sourceScenarioKey?: UserMediaWorkspaceScenarioKey;
  sourceScenarioLabel?: string;
  latestRequestId?: string;
  latestCorrelationId?: string;
};

type FileFocusRow = {
  fileId: string;
  relatedMediaArtifactId?: string;
  relatedMediaScenarioKey?: UserMediaWorkspaceScenarioKey;
};

const ALLOWED_SCENARIO_KEYS = new Set<UserMediaWorkspaceScenarioKey>([
  "avatar",
  "pet_profile",
  "daily_record",
  "receipt",
  "health_record",
  "community_post",
  "unknown",
]);

function isScenarioKey(value: string | null | undefined): value is UserMediaWorkspaceScenarioKey {
  return value !== undefined && value !== null && ALLOWED_SCENARIO_KEYS.has(value as UserMediaWorkspaceScenarioKey);
}

function resolveArtifactScenarioKey(
  artifact: MediaArtifactFocusRow | undefined,
): UserMediaWorkspaceScenarioKey | undefined {
  if (isScenarioKey(artifact?.sourceScenarioKey)) {
    return artifact.sourceScenarioKey;
  }

  return isScenarioKey(artifact?.sourceScenarioLabel) ? artifact.sourceScenarioLabel : undefined;
}

export function appendUserMediaWorkspaceFocus(
  params: URLSearchParams,
  focus: UserMediaWorkspaceFocus,
): URLSearchParams {
  const nextParams = new URLSearchParams(params);

  if (focus.artifactId) {
    nextParams.set("focusArtifactId", focus.artifactId);
  }

  if (focus.fileId) {
    nextParams.set("focusFileId", focus.fileId);
  }

  if (focus.scenarioKey) {
    nextParams.set("focusScenario", focus.scenarioKey);
  }

  return nextParams;
}

export function appendUserMediaWorkspaceLookupContext(
  params: URLSearchParams,
  context: {
    requestId?: string;
    correlationId?: string;
  },
  focus: UserMediaWorkspaceFocus | undefined,
): URLSearchParams {
  const nextParams = new URLSearchParams(params);

  if (context.requestId) {
    nextParams.set("requestId", context.requestId);
  }

  if (context.correlationId) {
    nextParams.set("correlationId", context.correlationId);
  }

  return focus ? appendUserMediaWorkspaceFocus(nextParams, focus) : nextParams;
}

export function readUserMediaWorkspaceFocus(
  params: URLSearchParams,
): UserMediaWorkspaceFocus | undefined {
  const artifactId = params.get("focusArtifactId") ?? undefined;
  const fileId = params.get("focusFileId") ?? undefined;
  const scenarioRaw = params.get("focusScenario");
  const scenarioKey = isScenarioKey(scenarioRaw) ? scenarioRaw : undefined;

  if (!artifactId && !fileId && !scenarioKey) {
    return undefined;
  }

  return {
    artifactId,
    fileId,
    scenarioKey,
  };
}

export function resolveUserMediaWorkspaceSelection(
  artifacts: readonly MediaArtifactFocusRow[],
  files: readonly FileFocusRow[],
  focus: UserMediaWorkspaceFocus | undefined,
): {
  artifactId?: string;
  scenarioKey?: UserMediaWorkspaceScenarioKey;
} | undefined {
  if (!focus) {
    return undefined;
  }

  const focusedArtifact = focus.artifactId
    ? artifacts.find((artifact) => artifact.artifactId === focus.artifactId)
    : undefined;
  const focusedFile = focus.fileId
    ? files.find((file) => file.fileId === focus.fileId)
    : undefined;

  const artifactId = focusedArtifact?.artifactId ?? focusedFile?.relatedMediaArtifactId;
  const scenarioKey =
    resolveArtifactScenarioKey(focusedArtifact) ??
    focusedFile?.relatedMediaScenarioKey ??
    focus.scenarioKey;

  if (!artifactId && !scenarioKey) {
    return undefined;
  }

  return {
    artifactId,
    scenarioKey,
  };
}

export function resolveContextualUserMediaWorkspaceSelection(
  artifacts: readonly MediaArtifactFocusRow[],
  focus: UserMediaWorkspaceFocus | undefined,
  context: {
    requestId?: string;
    correlationId?: string;
  },
): {
  artifactId?: string;
  scenarioKey?: UserMediaWorkspaceScenarioKey;
} | undefined {
  const scopedArtifacts = focus?.scenarioKey
    ? artifacts.filter((artifact) => resolveArtifactScenarioKey(artifact) === focus.scenarioKey)
    : artifacts;

  const requestMatchedArtifact = context.requestId
    ? scopedArtifacts.find((artifact) => artifact.latestRequestId === context.requestId)
    : undefined;

  if (requestMatchedArtifact) {
    return {
      artifactId: requestMatchedArtifact.artifactId,
      scenarioKey: resolveArtifactScenarioKey(requestMatchedArtifact) ?? focus?.scenarioKey,
    };
  }

  const correlationMatchedArtifact = context.correlationId
    ? scopedArtifacts.find((artifact) => artifact.latestCorrelationId === context.correlationId)
    : undefined;

  if (correlationMatchedArtifact) {
    return {
      artifactId: correlationMatchedArtifact.artifactId,
      scenarioKey: resolveArtifactScenarioKey(correlationMatchedArtifact) ?? focus?.scenarioKey,
    };
  }

  if (focus?.scenarioKey && scopedArtifacts.length > 0) {
    return {
      artifactId: scopedArtifacts[0]?.artifactId,
      scenarioKey: focus.scenarioKey,
    };
  }

  if (focus?.scenarioKey) {
    return {
      scenarioKey: focus.scenarioKey,
    };
  }

  return undefined;
}
